import { useEffect, useRef } from "react";

export type AntiCheatViolation = "tab_switch" | "window_blur";

const VIOLATION_LABELS: Record<AntiCheatViolation, string> = {
  tab_switch: "Cambiaste de pestaña / aplicación",
  window_blur: "La ventana perdió el foco",
};

export function violationLabel(v: AntiCheatViolation): string {
  return VIOLATION_LABELS[v];
}

interface Params {
  /** Si false, los listeners no se enganchan. */
  enabled: boolean;
  /** Callback con la razón de la violación. */
  onViolation: (v: AntiCheatViolation) => void;
}

/**
 * Detecta violaciones de pérdida de foco:
 *   - tab_switch: visibilitychange (cambio de tab, minimizar)
 *   - window_blur: window pierde foco (alt-tab, click en otra app)
 *
 * NO detecta fullscreen exit — eso es UI puro, no señal de cheating.
 *
 * Debounce: ignoramos eventos en los primeros 500ms después de habilitar
 * para evitar falsos positivos al iniciar el test.
 */
export function useAntiCheat({ enabled, onViolation }: Params) {
  const ignoreUntilRef = useRef<number>(0);
  const onViolationRef = useRef(onViolation);
  onViolationRef.current = onViolation;

  useEffect(() => {
    if (!enabled) return;

    ignoreUntilRef.current = Date.now() + 500;

    function fire(v: AntiCheatViolation) {
      if (Date.now() < ignoreUntilRef.current) return;
      onViolationRef.current(v);
    }

    function onVisibilityChange() {
      if (document.hidden) fire("tab_switch");
    }
    function onBlur() {
      fire("window_blur");
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("blur", onBlur);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("blur", onBlur);
    };
  }, [enabled]);
}

/** Requiere pantalla completa al elemento. */
export async function requestFullscreen(el?: HTMLElement): Promise<void> {
  const target = el ?? document.documentElement;
  if (target.requestFullscreen) {
    await target.requestFullscreen();
  }
}

export async function exitFullscreen(): Promise<void> {
  if (document.fullscreenElement && document.exitFullscreen) {
    await document.exitFullscreen();
  }
}
